class OmetriaFacetSync {
  constructor (config = {}) {
    this.config = {
      paginationAmount: config.paginationAmount || 24,
      defaultGridLayout: config.defaultGridLayout || 'medium',
      currencyId: config.currencyId,
      categoryId: config.categoryId,
      languageId: config.languageId,
    }

    this.drawerSelector = 'site-drawer[key="collection-filters"]'
    this.isSearchPage =
      document.body.classList.contains('template-search') ||
      window.location.pathname.includes('/search') ||
      window.location.pathname.includes('/pages/search-results') ||
      document.querySelector('#search-page-product-grid') !== null
    this.gridSelector = this.isSearchPage
      ? '#search-page-product-grid'
      : '#collection-page-product-grid'
    this.priceMinSelector = '#Filter-Drawer-Price-GTE'
    this.priceMaxSelector = '#Filter-Drawer-Price-LTE'
    this.applyButtonSelector = '.clc-ProductGridFacets_Apply'

    this.priceChangeTimeout = null

    this.filterRefreshTimeout = null
    this.pendingFilterRequestUrl = null
    this.delayedDrawerUpdateTimeout = null
    this.pendingFilterUrl = null

    this.drawerObserver = null
    this.drawerCloseObserver = null
    this.lastProductItemCount = null
    this.gridObserver = null
    this.bodyObserver = null

    window.omFacetSyncConfig = this.config

    this.isDrawerClosing = false

    this.setupListeners()
    this.setupGridLayoutObserver()
    this.setupBodyObserver()
    this.hoistDrawer()
    this.observeDrawerInGrid()

    window.omRefreshGridWith = urlString => {
      this.refreshGridWith(urlString)
    }
  }

  upgradeCustomElements (drawer) {
    try {
      if (!drawer) return
      if (customElements && customElements.upgrade) {
        const filterFacets = drawer.querySelectorAll(
          'details[is="filter-facet"]',
        )
        filterFacets.forEach(element => {
          try {
            if (customElements.get('filter-facet')) {
              customElements.upgrade(element)
            }
          } catch (error) {}
        })
        const priceRanges = drawer.querySelectorAll('price-range')
        priceRanges.forEach(element => {
          try {
            if (customElements.get('price-range')) {
              customElements.upgrade(element)
            }
          } catch (error) {}
        })
        const rangeSliders = drawer.querySelectorAll('range-slider')
        rangeSliders.forEach(element => {
          try {
            if (customElements.get('range-slider')) {
              customElements.upgrade(element)
            }
          } catch (error) {}
        })
        const facetSearches = drawer.querySelectorAll('facet-search')
        facetSearches.forEach(element => {
          try {
            if (customElements.get('facet-search')) {
              customElements.upgrade(element)
            }
          } catch (error) {}
        })
        const facetRemoves = drawer.querySelectorAll('facet-remove')
        facetRemoves.forEach(element => {
          try {
            if (customElements.get('facet-remove')) {
              customElements.upgrade(element)
              const link = element.querySelector(
                'a.clc-ProductGridFacets_ActiveLink',
              )
              if (link) {
                link.onclick = null
                const newLink = link.cloneNode(true)
                link.parentNode.replaceChild(newLink, link)
              }
            }
          } catch (error) {}
        })
      }
    } catch (error) {}
  }

  hoistDrawer () {
    try {
      const drawer = document.querySelector(this.drawerSelector)
      if (!drawer) return
      const grid = document.querySelector(this.gridSelector)
      if (grid && grid.contains(drawer)) {
        document.body.appendChild(drawer)
        this.upgradeCustomElements(drawer)
      }

      if (grid) {
        const productList = grid.querySelector(
          '[data-collection-filters-form-external-el="item-container"]',
        )
        if (productList) {
          const strayFacetRemoves = productList.querySelectorAll(
            'li > facet-remove.clc-ProductGridFacets_ActiveFacet',
          )
          strayFacetRemoves.forEach(facetRemove => {
            const isInDrawer = facetRemove.closest('site-drawer')
            if (!isInDrawer) {
              facetRemove.remove()
            }
          })
        }
      }
    } catch (error) {}
  }

  observeDrawerInGrid () {
    const grid = document.querySelector(this.gridSelector)
    if (!grid || this.drawerObserver) return

    this.drawerObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.addedNodes && mutation.addedNodes.length) {
          const drawer = document.querySelector(
            `${this.gridSelector} ${this.drawerSelector}`,
          )
          if (drawer) {
            this.hoistDrawer()
            setTimeout(() => {
              this.upgradeCustomElements(
                document.querySelector(this.drawerSelector),
              )
            }, 100)
          }
        }
      }
    })

    this.drawerObserver.observe(grid, { childList: true, subtree: true })
  }

  clearDrawerState () {
    document.body.classList.remove('drw-Drawers-active')
    document.body.classList.remove('drw-Drawers-collection-filters')
  }

  clearLoadingState () {
    try {
      const container = document.querySelector(
        '[data-collection-filters-form-external-el="container"]',
      )
      if (container) {
        container.setAttribute('aria-busy', 'false')
      }
      const form = document.querySelector('form[is="collection-filters-form"]')
      if (form && form.loading !== undefined) {
        form.loading = false
      }
    } catch (error) {}
  }

  getGridNode () {
    return document.querySelector(this.gridSelector)
  }

  getCurrentGridLayout () {
    const gridSwitcher = document.querySelector('grid-switcher')
    if (gridSwitcher) {
      const activeButton = gridSwitcher.querySelector(
        'button.clc-ProductGrid_GridButton-active[data-grid-view]',
      )
      if (activeButton && activeButton.dataset.gridView) {
        return activeButton.dataset.gridView
      }
    }

    const productList = document.querySelector('.prd-List_Items')
    if (productList) {
      if (productList.classList.contains('prd-List_Items-single')) {
        return 'single'
      }
      if (productList.classList.contains('prd-List_Items-small')) return 'small'
      if (productList.classList.contains('prd-List_Items-medium')) {
        return 'medium'
      }
      if (productList.classList.contains('prd-List_Items-large')) return 'large'
    }

    return this.config.defaultGridLayout
  }

  buildPageContext (urlString, qParamValue = '') {
    const pageContext = {
      currency_id:
        this.config.currencyId ||
        window.ometriaCurrency ||
        window.Shopify?.currency?.active,
      page_url: new URL(urlString, window.location.href).href,
    }

    const languageId = this.config.languageId || window.ometriaLanguageId
    if (languageId) pageContext.language_id = languageId

    if (this.isSearchPage) {
      if (qParamValue) pageContext.search_text = qParamValue
    } else if (this.config.categoryId) {
      pageContext.category_id = this.config.categoryId
    }

    return pageContext
  }

  reapplyGridLayout () {
    const gridSwitcher = document.querySelector('grid-switcher')
    if (!gridSwitcher) return

    const activeButton = gridSwitcher.querySelector(
      'button.clc-ProductGrid_GridButton-active[data-grid-view]',
    )
    if (!activeButton || !activeButton.dataset.gridView) return

    const layout = activeButton.dataset.gridView
    const productList = document.querySelector('.prd-List_Items')
    if (!productList) return

    const gridClasses = {
      single: 'prd-List_Items-single',
      small: 'prd-List_Items-small',
      medium: 'prd-List_Items-medium',
      large: 'prd-List_Items-large',
    }

    Object.values(gridClasses).forEach(className => {
      productList.classList.remove(className)
    })

    const layoutClass = gridClasses[layout]
    if (layoutClass) {
      productList.classList.add(layoutClass)
    }
  }

  updateActiveFacetsFromResponse () {
    try {
      const drawer = document.querySelector(this.drawerSelector)
      if (!drawer) return

      const grid = document.querySelector(this.gridSelector)
      if (grid) {
        const productList = grid.querySelector(
          '[data-collection-filters-form-external-el="item-container"]',
        )
        if (productList) {
          const strayFacetRemoves = productList.querySelectorAll(
            'li > facet-remove.clc-ProductGridFacets_ActiveFacet',
          )
          strayFacetRemoves.forEach(facetRemove => {
            const isInDrawer = facetRemove.closest('site-drawer')
            if (!isInDrawer) {
              facetRemove.remove()
            }
          })
        }
      }

      this.updateActivePillsFromDrawer(drawer)
      setTimeout(() => {
        this.upgradeCustomElements(drawer)
      }, 50)
    } catch (error) {}
  }

  reinitializeFacetSearch () {
    try {
      if (window.omFacetSearchInit) {
        setTimeout(() => {
          window.omFacetSearchInit()
        }, 50)
        setTimeout(() => {
          window.omFacetSearchInit()
        }, 200)
        setTimeout(() => {
          window.omFacetSearchInit()
        }, 500)
      } else {
        const drawer = document.querySelector(this.drawerSelector)
        if (drawer) {
          const facetSearches = drawer.querySelectorAll('facet-search')
          facetSearches.forEach(facetSearch => {
            try {
              if (customElements && customElements.get('facet-search')) {
                if (customElements.upgrade) {
                  customElements.upgrade(facetSearch)
                }
                if (
                  facetSearch.connectedCallback &&
                  !facetSearch._initialized
                ) {
                  facetSearch.connectedCallback()
                }
              }
            } catch (error) {}
          })
        }
      }
    } catch (error) {}
  }

  tryGrapheneRefresh (urlString, keepDrawerOpen = false) {
    const target = this.getGridNode()
    if (!target) {
      window.location.href = urlString
      return
    }

    const beforeHTML = target.innerHTML
    const currentLayout = this.getCurrentGridLayout()

    if (keepDrawerOpen) {
      this.hoistDrawer()
      document.body.classList.add('drw-Drawers-active')
      document.body.classList.add('drw-Drawers-collection-filters')
      const drawer = document.querySelector(this.drawerSelector)
      if (drawer) {
        drawer.classList.add('drw-Drawer-active')
      }
    }

    const keepDrawerOpenCallback = () => {
      if (!keepDrawerOpen || this.isDrawerClosing) return

      this.hoistDrawer()
      document.body.classList.add('drw-Drawers-active')
      document.body.classList.add('drw-Drawers-collection-filters')
      const drawer = document.querySelector(this.drawerSelector)
      if (drawer) {
        drawer.classList.add('drw-Drawer-active')
      }
      this.upgradeCustomElements(drawer)
    }

    if (window.graphenehc?.load) {
      const templateId = this.isSearchPage ? 'search' : 'category'

      const searchParams = new URLSearchParams(urlString.split('?')[1] || '')
      const qParamValue = searchParams.get('q') || ''

      const pageContext = this.buildPageContext(urlString, qParamValue)

      clearTimeout(this.delayedDrawerUpdateTimeout)
      this.delayedDrawerUpdateTimeout = null
      this.pendingFilterRequestUrl = urlString
      window.graphenehc.load({
        template: { id: templateId, target: this.gridSelector },
        include: ['breadcrumb', 'page'],
        url: urlString,
        args: {
          query: urlString,
          per_page: this.config.paginationAmount,
          grid_layout: currentLayout,
        },
        page_context: pageContext,
        success: response => {
          if (this.pendingFilterRequestUrl !== urlString) return

          const targetElement = document.querySelector(this.gridSelector)
          const html =
            response.templates?.[templateId]?.html ||
            response.templates?.category?.html
          if (!targetElement || !html) return

          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = html

          const productContent =
            tempDiv.querySelector('.clc-ProductGrid_Products') ||
            tempDiv.querySelector('.prd-List') ||
            tempDiv.querySelector(
              '[data-collection-filters-form-external-el="item-container"]',
            )

          if (!keepDrawerOpen) {
            const filterDrawer = tempDiv.querySelector(
              'site-drawer[key="collection-filters"]',
            )
            const existingDrawer = document.querySelector(this.drawerSelector)
            if (filterDrawer && existingDrawer) {
              const existingBody = existingDrawer.querySelector(
                '.drw-Drawer_Body',
              )
              const newBody = filterDrawer.querySelector('.drw-Drawer_Body')
              if (existingBody && newBody) {
                existingBody.innerHTML = newBody.innerHTML
                this.syncCheckboxesFromUrl(existingDrawer, urlString)
              }
            }
          }

          if (productContent) {
            targetElement.innerHTML = productContent.outerHTML
          } else if (!keepDrawerOpen) {
            const filterDrawer = tempDiv.querySelector(
              'site-drawer[key="collection-filters"]',
            )
            filterDrawer?.remove()
            targetElement.innerHTML = tempDiv.innerHTML
          }

          if (keepDrawerOpen) {
            clearTimeout(this.delayedDrawerUpdateTimeout)
            const capturedUrl = urlString
            this.delayedDrawerUpdateTimeout = setTimeout(() => {
              this.delayedDrawerUpdateTimeout = null
              if (this.pendingFilterRequestUrl !== capturedUrl) return
              const existingDrawer = document.querySelector(this.drawerSelector)
              const newBody = tempDiv.querySelector(
                'site-drawer[key="collection-filters"] .drw-Drawer_Body',
              )
              const existingBody = existingDrawer?.querySelector(
                '.drw-Drawer_Body',
              )
              if (existingBody && newBody) {
                existingBody.innerHTML = newBody.innerHTML
                this.syncCheckboxesFromUrl(existingDrawer, capturedUrl)
                this.updateActiveFacetsFromResponse()
                this.reinitializeFacetSearch()
              }
            }, 600)
          }

          if (this.isSearchPage) {
            this.updateSearchSummaryFromGrid()
          }
          setTimeout(() => {
            this.clearLoadingState()
            keepDrawerOpenCallback()
            this.reapplyGridLayout()
            this.updateActiveFacetsFromResponse()
            this.reinitializeFacetSearch()
            this.updateSortLabel()
            this.updateProductCount(response)
            this.updateSearchSummaryFromGrid()
            const currentUrl = new URL(window.location.href)
            this.updateLoadMoreButton(currentUrl)
            const productList = document.querySelector('.prd-List_Items')
            if (productList) {
              const products = productList.querySelectorAll(
                '[data-collection-filters-form-external-el="item"]',
              )
              products.forEach((product, index) => {
                product.setAttribute('data-page', '1')
              })
            }
          }, 100)
        },
      })
    }

    setTimeout(() => {
      const afterFirst = this.getGridNode()?.innerHTML
      if (afterFirst && afterFirst !== beforeHTML) return

      if (window.graphenehc?.load) {
        const templateId = this.isSearchPage ? 'search' : 'category'

        const searchParams = new URLSearchParams(urlString.split('?')[1] || '')
        const qParamValue = searchParams.get('q') || ''

        const pageContext = this.buildPageContext(urlString, qParamValue)
        pageContext.url = urlString

        this.pendingFilterRequestUrl = urlString
        window.graphenehc.load({
          template: { id: templateId, target: this.gridSelector },
          include: ['breadcrumb', 'page'],
          url: urlString,
          args: {
            query: urlString,
            per_page: this.config.paginationAmount,
            grid_layout: this.getCurrentGridLayout(),
          },
          page_context: pageContext,
          success: response => {
            if (this.pendingFilterRequestUrl !== urlString) return

            const targetElement = document.querySelector(this.gridSelector)
            const html =
              response.templates?.[templateId]?.html ||
              response.templates?.category?.html
            if (targetElement && html) {
              const tempDiv = document.createElement('div')
              tempDiv.innerHTML = html
              const productContent =
                tempDiv.querySelector('.clc-ProductGrid_Products') ||
                tempDiv.querySelector('.prd-List') ||
                tempDiv.querySelector(
                  '[data-collection-filters-form-external-el="item-container"]',
                )
              if (productContent) {
                targetElement.innerHTML = productContent.outerHTML
              } else {
                const filterDrawer = tempDiv.querySelector(
                  'site-drawer[key="collection-filters"]',
                )
                const existingDrawer = document.querySelector(
                  this.drawerSelector,
                )
                if (filterDrawer && existingDrawer) {
                  const existingBody = existingDrawer.querySelector(
                    '.drw-Drawer_Body',
                  )
                  const newBody = filterDrawer.querySelector('.drw-Drawer_Body')
                  if (existingBody && newBody) {
                    existingBody.innerHTML = newBody.innerHTML
                    this.syncCheckboxesFromUrl(existingDrawer, urlString)
                  }
                }
                filterDrawer?.remove()
                targetElement.innerHTML = tempDiv.innerHTML
              }
              if (this.isSearchPage) {
                this.updateSearchSummaryFromGrid()
              }
            }
            setTimeout(() => {
              this.clearLoadingState()
              keepDrawerOpenCallback()
              this.reapplyGridLayout()
              this.updateActiveFacetsFromResponse()
              this.reinitializeFacetSearch()
              this.updateSortLabel()
              this.updateProductCount(response)
              if (this.isSearchPage) {
                this.updateSearchSummaryFromGrid()
              }
            }, 100)
          },
        })
      }
    }, 150)

    setTimeout(() => {
      const afterSecond = this.getGridNode()?.innerHTML
      if (afterSecond && afterSecond !== beforeHTML) return
      window.location.href = urlString
    }, 400)

    if (!keepDrawerOpen) {
      this.hoistDrawer()
      this.observeDrawerInGrid()
      this.clearDrawerState()
    } else {
      this.observeDrawerInGrid()
    }
    setTimeout(() => {
      this.clearLoadingState()
      keepDrawerOpenCallback()
      this.reapplyGridLayout()
      this.updateActiveFacetsFromResponse()
      this.reinitializeFacetSearch()
      this.updateSortLabel()
      this.updateProductCount()
    }, 200)

    setTimeout(() => {
      this.clearLoadingState()
      this.reapplyGridLayout()
      this.updateActiveFacetsFromResponse()
      this.reinitializeFacetSearch()
      keepDrawerOpenCallback()
      this.updateSortLabel()
      this.updateProductCount()
    }, 500)
  }

  updateSearchSummaryFromGrid () {
    try {
      if (!this.isSearchPage) return

      const grid = document.querySelector(this.gridSelector)
      if (!grid) return

      const header = document.querySelector('.clc-ProductGrid_Header')
      if (!header) return

      const summaryInGrid = grid.querySelector('.clc-ProductGrid_SearchSummary')

      if (summaryInGrid) {
        const summaryHTML = summaryInGrid.outerHTML

        document
          .querySelectorAll('.clc-ProductGrid_SearchSummary')
          .forEach(el => el.remove())

        header.insertAdjacentHTML('beforebegin', summaryHTML)
      }
    } catch (error) {
      console.warn('Error updating search summary:', error)
    }
  }

  refreshGridWith (urlString, keepDrawerOpen = false) {
    if (!urlString) return
    this.pendingFilterUrl = null
    if (urlString !== window.location.href) {
      window.history.pushState({}, '', urlString)
    }
    if (!keepDrawerOpen) {
      this.clearDrawerState()
    }
    const url = new URL(urlString)
    url.searchParams.delete('page')
    this.tryGrapheneRefresh(url.toString(), keepDrawerOpen)
  }

  deduplicateUrlParams (urlString) {
    try {
      const url = new URL(urlString)
      const params = url.searchParams

      const filterKeys = Array.from(params.keys()).filter(
        key => key.indexOf('filter.v.') === 0,
      )

      filterKeys.forEach(key => {
        const allValues = params.getAll(key)
        const uniqueValues = [...new Set(allValues)]
        params.delete(key)
        uniqueValues.forEach(value => params.append(key, value))
      })

      return url.toString()
    } catch (error) {
      return urlString
    }
  }

  syncCheckboxesFromUrl (drawer, urlString) {
    try {
      const url = new URL(urlString)
      const params = url.searchParams
      const checkboxes = drawer?.querySelectorAll(
        'input[type="checkbox"].clc-ProductGridFacet_Checkbox',
      )
      if (!checkboxes?.length) return

      checkboxes.forEach(checkbox => {
        const name = checkbox.name
        const value = checkbox.value
        if (!name || !value) return
        const urlValues = [
          ...params.getAll(name),
          ...params.getAll(`filter.v.${name}`),
          ...params.getAll(name.replace(/^filter\.v\./, '')),
        ]
        checkbox.checked = urlValues.includes(value)
      })
    } catch (err) {
      console.warn('syncCheckboxesFromUrl:', err)
    }
  }

  buildUrlFromDrawer (drawer, changedInputName) {
    const url = new URL(window.location.href)
    const params = url.searchParams

    params.delete('page')

    if (changedInputName) {
      params.delete(changedInputName)
      params.delete(`filter.v.${changedInputName}`)

      const uniqueValues = new Set()
      const addChecked = list => {
        list?.forEach(el => {
          if (el?.value) uniqueValues.add(el.value)
        })
      }
      addChecked(
        drawer.querySelectorAll(
          `input[type="checkbox"][name="${changedInputName}"]:checked`,
        ),
      )
      addChecked(
        drawer.querySelectorAll(
          `input[type="checkbox"][name="filter.v.${changedInputName}"]:checked`,
        ),
      )
      uniqueValues.forEach(value => params.append(changedInputName, value))
    } else {
      Array.from(params.keys()).forEach(key => {
        if (key.indexOf('filter.v.') === 0) params.delete(key)
      })

      const filterMap = new Map()
      drawer
        .querySelectorAll('input[type="checkbox"]:checked')
        .forEach(element => {
          if (!element.name || !element.value) return
          if (!filterMap.has(element.name)) {
            filterMap.set(element.name, new Set())
          }
          filterMap.get(element.name).add(element.value)
        })
      filterMap.forEach((values, filterName) => {
        values.forEach(value => params.append(filterName, value))
      })
    }

    return url.toString()
  }

  updateActivePillsFromDrawer (drawer) {
    try {
      const currentDrawer =
        drawer && drawer.isConnected
          ? drawer
          : document.querySelector(this.drawerSelector)
      if (!currentDrawer) return

      const activeFacetsContainer = currentDrawer.querySelector(
        '[data-collection-filters-form-el="active-facets"]',
      )
      if (!activeFacetsContainer) return

      const url = new URL(window.location.href)
      const params = url.searchParams

      activeFacetsContainer.innerHTML = ''

      const createdPills = new Set()

      Array.from(params.keys()).forEach(filterName => {
        if (
          filterName === 'page' ||
          filterName === 'sort' ||
          filterName === 'price'
        ) {
          return
        }

        const allFilterValues = params.getAll(filterName)
        const uniqueFilterValues = [...new Set(allFilterValues)]

        uniqueFilterValues.forEach(filterValue => {
          const pillKey = `${filterName}:${filterValue}`

          if (createdPills.has(pillKey)) {
            return
          }

          createdPills.add(pillKey)

          let checkbox = currentDrawer.querySelector(
            `input[type="checkbox"][name="${filterName}"][value="${filterValue}"]`,
          )

          if (!checkbox && filterName.indexOf('filter.v.') === 0) {
            const baseName = filterName.replace(/^filter\.v\./, '')
            checkbox = currentDrawer.querySelector(
              `input[type="checkbox"][name="${baseName}"][value="${filterValue}"]`,
            )
          }

          if (!checkbox && filterName.indexOf('filter.v.') !== 0) {
            checkbox = currentDrawer.querySelector(
              `input[type="checkbox"][name="filter.v.${filterName}"][value="${filterValue}"]`,
            )
          }

          if (!checkbox) return

          const label = checkbox.closest('label.clc-ProductGridFacet_Label')
          if (!label) return

          const valueSpan = label.querySelector('.clc-ProductGridFacet_Value')
          if (!valueSpan) return

          const filterLabel = valueSpan.textContent
            .trim()
            .replace(/\s*\(\d+\)$/, '')

          const facetItem = checkbox.closest('.clc-ProductGridFacet')
          const facetSummary = facetItem?.querySelector(
            '.clc-ProductGridFacet_FilterLabel',
          )
          const facetTitle = facetSummary?.textContent.trim() || filterName

          const removeUrl = new URL(window.location.href)
          const removeParams = removeUrl.searchParams
          removeParams.delete('page')

          const currentValues = [...new Set(removeParams.getAll(filterName))]
          const newValues = currentValues.filter(v => v !== filterValue)
          removeParams.delete(filterName)
          newValues.forEach(v => removeParams.append(filterName, v))

          const existingPills = activeFacetsContainer.querySelectorAll(
            'facet-remove',
          )
          const alreadyExists = Array.from(existingPills).some(pill => {
            const existingLink = pill.querySelector(
              'a.clc-ProductGridFacets_ActiveLink',
            )
            if (!existingLink) return false
            const existingUrl = new URL(
              existingLink.getAttribute('href'),
              window.location.href,
            )
            const existingParams = existingUrl.searchParams
            const existingValues = [
              ...new Set(existingParams.getAll(filterName)),
            ]
            const currentValues = [...new Set(removeParams.getAll(filterName))]
            return (
              existingValues.length === currentValues.length - 1 &&
              existingValues.every(v => currentValues.includes(v)) &&
              currentValues.filter(v => !existingValues.includes(v))[0] ===
                filterValue
            )
          })

          if (alreadyExists) {
            return
          }

          const li = document.createElement('li')
          const escapedLabel = filterLabel
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
          const escapedTitle = facetTitle
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
          li.innerHTML = `
            <facet-remove class="clc-ProductGridFacets_ActiveFacet" data-collection-filters-form-el="active-facet">
              <a href="${removeUrl.toString()}" class="clc-ProductGridFacets_ActiveLink" title="Remove ${escapedTitle} ${escapedLabel}" role="button">
                ${escapedLabel}
                <svg class="clc-ProductGridFacets_ActiveLinkIcon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 11 10" width="11" height="10">
                  <path fill="currentColor" d="m1.695.308 8.975 8.975-.716.716L.979 1.024z"/>
                  <path fill="currentColor" d="M10.668.715 1.692 9.691l-.716-.717L9.951 0z"/>
                </svg>
              </a>
            </facet-remove>
          `
          if (activeFacetsContainer) {
            activeFacetsContainer.appendChild(li)
          }
        })
      })

      const priceParam = params.get('price')
      if (priceParam) {
        const [min, max] = priceParam.split('~')

        const priceMinInput =
          currentDrawer.querySelector(this.priceMinSelector) ||
          currentDrawer.querySelector('input[name="price_min"]') ||
          currentDrawer.querySelector('#Filter-Drawer-Price-GTE')
        const priceMaxInput =
          currentDrawer.querySelector(this.priceMaxSelector) ||
          currentDrawer.querySelector('input[name="price_max"]') ||
          currentDrawer.querySelector('#Filter-Drawer-Price-LTE')

        let displayMin = null
        let displayMax = null

        if (priceMinInput && priceMinInput.value !== '') {
          displayMin = parseFloat(priceMinInput.value)
        } else if (min && min.trim() !== '') {
          displayMin = parseFloat(min.trim())
        }

        if (priceMaxInput && priceMaxInput.value !== '') {
          displayMax = parseFloat(priceMaxInput.value)
        } else if (max && max.trim() !== '') {
          displayMax = parseFloat(max.trim())
        }

        const container = priceMinInput
          ? priceMinInput.closest('[data-price-range-container]')
          : null
        const maxRange = container
          ? parseFloat(container.getAttribute('data-max')) || 0
          : priceMaxInput
            ? parseFloat(priceMaxInput.getAttribute('max')) || 0
            : 0

        if (displayMin === null) displayMin = 0
        if (displayMax === null) displayMax = maxRange

        if (
          priceMinInput &&
          priceMaxInput &&
          !isNaN(displayMin) &&
          !isNaN(displayMax)
        ) {
          const currencySymbol =
            priceMinInput.getAttribute('data-currency') || '£'
          const minFormatted = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
            .format(displayMin)
            .replace('£', currencySymbol)
          const maxFormatted = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
            .format(displayMax)
            .replace('£', currencySymbol)

          const removeUrl = new URL(window.location.href)
          const removeParams = removeUrl.searchParams
          removeParams.delete('page')
          removeParams.delete('price')

          const li = document.createElement('li')
          const priceLabel = `${minFormatted} - ${maxFormatted}`
          li.innerHTML = `
            <facet-remove class="clc-ProductGridFacets_ActiveFacet" data-collection-filters-form-el="active-facet">
              <a href="${removeUrl.toString()}" class="clc-ProductGridFacets_ActiveLink" title="Remove Price ${priceLabel}" role="button">
                ${priceLabel}
                <svg class="clc-ProductGridFacets_ActiveLinkIcon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 11 10" width="11" height="10">
                  <path fill="currentColor" d="m1.695.308 8.975 8.975-.716.716L.979 1.024z"/>
                  <path fill="currentColor" d="M10.668.715 1.692 9.691l-.716-.717L9.951 0z"/>
                </svg>
              </a>
            </facet-remove>
          `
          if (activeFacetsContainer) {
            activeFacetsContainer.appendChild(li)
          }
        }
      }

      if (activeFacetsContainer && customElements && customElements.upgrade) {
        activeFacetsContainer.querySelectorAll('facet-remove').forEach(el => {
          try {
            if (customElements.get('facet-remove')) {
              customElements.upgrade(el)
              const link = el.querySelector(
                'a.clc-ProductGridFacets_ActiveLink',
              )
              if (link) {
                const newLink = link.cloneNode(true)
                link.parentNode.replaceChild(newLink, link)
              }
            }
          } catch (error) {}
        })
      }
    } catch (error) {}
  }

  handleCheckboxChange (event) {
    let input = event.target

    if (input.type !== 'checkbox') {
      const label = input.closest('label.clc-ProductGridFacet_Label')
      if (label) {
        input = label.querySelector('input[type="checkbox"]')
      } else {
        return
      }
    }

    if (!input || input.type !== 'checkbox') return

    if (input.getAttribute('data-ometria-removing') === 'true') {
      return
    }

    const isFilterCheckbox =
      input.classList.contains('clc-ProductGridFacet_Checkbox') ||
      input.closest('.clc-ProductGridFacet_Item') !== null

    if (!isFilterCheckbox) return

    let drawer = input.closest(this.drawerSelector)
    if (!drawer) {
      drawer = document.querySelector(this.drawerSelector)
    }
    if (!drawer) return

    const url = this.buildUrlFromDrawer(drawer, input.name)
    const cleanUrl = this.deduplicateUrlParams(url)
    window.history.pushState({}, '', cleanUrl)
    this.updateActivePillsFromDrawer(drawer)
    this.pendingFilterUrl = cleanUrl
  }

  handleApplyButtonClick (event) {
    const button = event.target.closest(this.applyButtonSelector)
    if (!button) return

    const drawer = button.closest(this.drawerSelector)
    if (!drawer) return

    event.preventDefault()

    this.pendingFilterUrl = this.buildApplyUrlFromDrawer(drawer)
    try {
      window.drawers && window.drawers.closeDrawer()
    } catch (error) {}
  }

  handlePriceInputsChange (event) {
    const target = event.target

    if (
      !target.matches(this.priceMinSelector) &&
      !target.matches(this.priceMaxSelector)
    ) {
      return
    }

    const drawer = target.closest(this.drawerSelector)
    if (!drawer) return

    const minElement = drawer.querySelector(this.priceMinSelector)
    const maxElement = drawer.querySelector(this.priceMaxSelector)

    const url = new URL(window.location.href)
    const params = url.searchParams

    params.delete('filter.v.price.gte')
    params.delete('filter.v.price.lte')
    params.delete('price')
    params.delete('page')

    const minValue =
      minElement && minElement.value !== ''
        ? parseFloat(minElement.value)
        : null
    const maxValue =
      maxElement && maxElement.value !== ''
        ? parseFloat(maxElement.value)
        : null

    if (minValue !== null || maxValue !== null) {
      const container = minElement
        ? minElement.closest('[data-price-range-container]')
        : null
      const maxRange = container
        ? parseFloat(container.getAttribute('data-max')) || 0
        : 0

      const finalMin = minValue !== null ? Math.round(minValue) : null
      const finalMax = maxValue !== null ? Math.round(maxValue) : null

      if (!(finalMin === 0 && finalMax === maxRange)) {
        if (finalMin !== null && finalMax !== null) {
          params.set('price', `${finalMin}~${finalMax}`)
        } else if (finalMin !== null) {
          params.set('price', `${finalMin}~`)
        } else if (finalMax !== null) {
          params.set('price', `~${finalMax}`)
        }
      }
    }

    const cleanUrl = this.deduplicateUrlParams(url.toString())

    if (cleanUrl !== window.location.href) {
      window.history.pushState({}, '', cleanUrl)
    }

    const currentDrawer = document.querySelector(this.drawerSelector) || drawer
    requestAnimationFrame(() => {
      this.updateActivePillsFromDrawer(currentDrawer)
    })

    this.pendingFilterUrl = cleanUrl
  }

  updateProductCount (grapheneResponse) {
    try {
      let countDataEl = null
      let totalCount = null
      let displayOne = null
      let displayOther = null

      if (grapheneResponse && typeof grapheneResponse === 'string') {
        const parser = new DOMParser()
        const doc = parser.parseFromString(grapheneResponse, 'text/html')
        countDataEl = doc.querySelector('[data-product-grid-count-data]')
      }

      if (!countDataEl) {
        countDataEl = document.querySelector('[data-product-grid-count-data]')
      }

      if (countDataEl) {
        totalCount = countDataEl.dataset.count
        displayOne = countDataEl.dataset.displayOne
        displayOther = countDataEl.dataset.displayOther
      }

      const products = document.querySelectorAll(
        '[data-collection-filters-form-external-el="item"]',
      )
      const productCount = products.length

      const countElements = document.querySelectorAll(
        '[data-product-grid-count]',
      )

      if (
        countElements.length > 0 &&
        totalCount &&
        (displayOne || displayOther)
      ) {
        const template = productCount === 1 ? displayOne : displayOther
        if (template) {
          const countText = template
            .replace(/\{\{\s*product_count\s*\}\}/g, productCount)
            .replace(/\{\{\s*count\s*\}\}/g, totalCount)

          countElements.forEach(element => {
            element.innerHTML = countText
          })
        }
      } else if (countElements.length > 0 && totalCount) {
        countElements.forEach(element => {
          element.textContent = `${productCount} of ${totalCount} items`
        })
      }

      const footerCount = document.querySelector(
        '[data-collection-filters-form-external-el="footer-count"]',
      )
      if (footerCount && totalCount) {
        footerCount.setAttribute(
          'aria-hidden',
          Number(productCount) === Number(totalCount) ? 'true' : 'false',
        )
      }
    } catch (error) {
      console.warn('Error updating product count:', error)
    }
  }

  updateSortLabel () {
    const sortSelect = document.getElementById('SortBy')
    if (!sortSelect) return

    const selectedOption = sortSelect.options[sortSelect.selectedIndex]
    const selectedText = selectedOption ? selectedOption.textContent.trim() : ''

    if (selectedText) {
      const sortLabel = document.querySelector(
        '[data-collection-filters-form-el="sort-label"]',
      )
      if (sortLabel) {
        let desktopLabel = sortLabel.querySelector(
          '.clc-ProductGridFacets_DesktopLabel',
        )
        if (!desktopLabel) {
          desktopLabel = document.createElement('span')
          desktopLabel.className = 'clc-ProductGridFacets_DesktopLabel'
          sortLabel.appendChild(desktopLabel)
        }
        desktopLabel.textContent = `: ${selectedText}`
      }
    }
  }

  handleSortChange (event) {
    if (event.target.id !== 'SortBy') return
    const sortSelect = event.target

    const sortValue = sortSelect.value
    if (!sortValue) return
    this.updateSortLabel()

    let ometriaSort = ''
    if (sortValue === 'manual' || sortValue === 'relevance') {
      ometriaSort = ''
    } else if (sortValue === 'best-selling') {
      ometriaSort = 'sales~desc'
    } else {
      const parts = sortValue.split('-')
      if (parts.length >= 2) {
        const field = parts[0]
        const direction =
          parts[parts.length - 1] === 'ascending' ? 'asc' : 'desc'
        ometriaSort = `${field}~${direction}`
      } else {
        ometriaSort = sortValue
      }
    }

    const url = new URL(window.location.href)
    const params = url.searchParams

    params.delete('page')

    if (ometriaSort) {
      params.set('sort', ometriaSort)
    } else {
      params.delete('sort')
    }

    const cleanUrl = this.deduplicateUrlParams(url.toString())
    this.refreshGridWith(cleanUrl)
  }

  setupGridLayoutObserver () {
    if (!window.MutationObserver || !document.body) return

    this.isReapplyingLayout = false
    this.gridLayoutDebounceId = null
    this.gridObserver = new MutationObserver(mutations => {
      let shouldReapply = false
      mutations.forEach(mutation => {
        if (this.isReapplyingLayout) {
          return
        }
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          for (let index = 0; index < mutation.addedNodes.length; index++) {
            const node = mutation.addedNodes[index]
            if (node.nodeType === 1) {
              if (
                (node.matches && node.matches('.prd-List_Items')) ||
                (node.querySelector && node.querySelector('.prd-List_Items'))
              ) {
                shouldReapply = true
              }
            }
          }
        }
        if (mutation.type === 'attributes' && mutation.target) {
          const productList =
            mutation.target.closest &&
            mutation.target.closest('.prd-List_Items')
          if (productList) {
            shouldReapply = true
          }
        }
      })
      if (shouldReapply) {
        if (this.gridLayoutDebounceId) clearTimeout(this.gridLayoutDebounceId)
        this.gridLayoutDebounceId = setTimeout(() => {
          this.gridLayoutDebounceId = null
          this.isReapplyingLayout = true
          this.reapplyGridLayout()
          try {
            const productList = document.querySelector('.prd-List_Items')
            const currentCount = productList
              ? productList.querySelectorAll(
                '[data-collection-filters-form-external-el="item"]',
              ).length
              : 0
            if (currentCount !== this.lastProductItemCount) {
              this.lastProductItemCount = currentCount
              const currentUrl = new URL(window.location.href)
              this.updateLoadMoreButton(currentUrl)
            }
          } catch (e) {}
          this.isReapplyingLayout = false
        }, 250)
      }
    })

    const gridContainer = document.querySelector(this.gridSelector)
    if (gridContainer) {
      this.gridObserver.observe(gridContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      })
    }
  }

  setupBodyObserver () {
    if (!window.MutationObserver || !document.body) return

    this.bodyObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.addedNodes && mutation.addedNodes.length) {
          for (let index = 0; index < mutation.addedNodes.length; index++) {
            const node = mutation.addedNodes[index]
            if (node.nodeType === 1) {
              if (node.matches && node.matches(this.drawerSelector)) {
                setTimeout(() => {
                  this.upgradeCustomElements(node)
                }, 100)
              } else if (
                node.querySelector &&
                node.querySelector(this.drawerSelector)
              ) {
                setTimeout(() => {
                  this.upgradeCustomElements(
                    node.querySelector(this.drawerSelector),
                  )
                }, 100)
              }
            }
          }
        }
        if (mutation.type === 'attributes' && mutation.target) {
          const drawer =
            mutation.target.closest &&
            mutation.target.closest(this.drawerSelector)
          if (
            drawer &&
            (mutation.attributeName === 'open' ||
              mutation.attributeName === 'class')
          ) {
            setTimeout(() => {
              this.upgradeCustomElements(drawer)
            }, 100)
          }
        }
      }
    })

    this.bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'class'],
    })
  }

  setupDrawerCloseListener () {
    document.addEventListener('drawers:closing', () => {
      this.isDrawerClosing = true
      this.clearDrawerState()

      let url = this.pendingFilterUrl
      if (!url) {
        const drawer = document.querySelector(this.drawerSelector)
        const built = drawer && this.buildApplyUrlFromDrawer(drawer)
        if (built && built !== window.location.href) url = built
      }
      if (url) {
        this.pendingFilterUrl = null
        this.refreshGridWith(url, false)
      }

      setTimeout(() => {
        this.isDrawerClosing = false
      }, 300)
    })
  }

  buildApplyUrlFromDrawer (drawer) {
    const url = new URL(window.location.href)
    const params = url.searchParams
    params.delete('filter.v.price.gte')
    params.delete('filter.v.price.lte')
    params.delete('price')
    params.delete('page')

    const minElement = drawer.querySelector(this.priceMinSelector)
    const maxElement = drawer.querySelector(this.priceMaxSelector)
    const minValue =
      minElement && minElement.value !== ''
        ? parseFloat(minElement.value)
        : null
    const maxValue =
      maxElement && maxElement.value !== ''
        ? parseFloat(maxElement.value)
        : null

    if (minValue !== null || maxValue !== null) {
      const container = minElement
        ? minElement.closest('[data-price-range-container]')
        : null
      const maxRange = container
        ? parseFloat(container.getAttribute('data-max')) || 0
        : 0
      const finalMin = minValue !== null ? Math.round(minValue) : null
      const finalMax = maxValue !== null ? Math.round(maxValue) : null

      if (!(finalMin === 0 && finalMax === maxRange)) {
        if (finalMin !== null && finalMax !== null) {
          params.set('price', `${finalMin}~${finalMax}`)
        } else if (finalMin !== null) {
          params.set('price', `${finalMin}~`)
        } else if (finalMax !== null) {
          params.set('price', `~${finalMax}`)
        }
      }
    }

    return this.deduplicateUrlParams(url.toString())
  }

  handleDrawerTriggerClick (event) {
    const trigger = event.target.closest(
      '[data-drawers-trigger="collection-filters"]',
    )
    if (!trigger) return

    const drawer = document.querySelector(this.drawerSelector)
    if (!drawer) return

    const isOpening = !drawer.classList.contains('drw-Drawer-active')
    if (!isOpening) return

    const runReinit = () => {
      try {
        const currentDrawer = document.querySelector(this.drawerSelector)
        if (!currentDrawer) return

        this.upgradeCustomElements(currentDrawer)
        this.updateActiveFacetsFromResponse()
        if (window.omFacetSearchInit) {
          window.omFacetSearchInit()
        }
        this.reinitializeFacetSearch()

        const facetContents = currentDrawer.querySelectorAll(
          '[data-filter-facet-el="content"]',
        )
        facetContents.forEach(el => {
          if (el?.style) {
            el.style.height = ''
            el.style.overflow = ''
          }
        })
      } catch (err) {
        console.warn('Ometria filter drawer reinit:', err)
      }
    }

    runReinit()
    requestAnimationFrame(() => runReinit())
  }

  handlePopState () {
    this.refreshGridWith(window.location.href)
    setTimeout(() => {
      this.updateSortLabel()
    }, 200)
  }

  handleFacetRemoveClick (event) {
    const link = event.target.closest('a.clc-ProductGridFacets_ActiveLink')
    if (!link) return

    const facetRemove = event.target.closest('facet-remove')
    if (!facetRemove) return

    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()

    if (facetRemove.querySelector && facetRemove.querySelector('a')) {
      const nativeLink = facetRemove.querySelector('a')
      if (nativeLink.onclick) {
        nativeLink.onclick = null
      }
    }

    const linkClone = link.cloneNode(true)
    const svgElements = linkClone.querySelectorAll('svg')
    svgElements.forEach(svg => svg.remove())
    const linkText = linkClone.textContent.trim()

    try {
      const drawer = document.querySelector(this.drawerSelector)
      if (!drawer) return

      let activeFacetsContainer = facetRemove.closest(
        '[data-collection-filters-form-el="active-facets"]',
      )

      if (!activeFacetsContainer) {
        activeFacetsContainer = drawer.querySelector(
          '[data-collection-filters-form-el="active-facets"]',
        )
      }

      if (!activeFacetsContainer) {
        activeFacetsContainer = document.querySelector(
          '[data-collection-filters-form-el="active-facets"]',
        )
      }

      const currentUrl = new URL(window.location.href)
      const currentParams = currentUrl.searchParams

      let removedFilterName = null
      let removedFilterValue = null

      const drawerCheckboxes = drawer.querySelectorAll(
        'input[type="checkbox"].clc-ProductGridFacet_Checkbox',
      )
      for (const checkbox of drawerCheckboxes) {
        const label = checkbox.closest('label.clc-ProductGridFacet_Label')
        if (!label) continue

        const valueSpan = label.querySelector('.clc-ProductGridFacet_Value')
        if (!valueSpan) continue

        const filterLabel = valueSpan.textContent
          .trim()
          .replace(/\s*\(\d+\)$/, '')

        if (filterLabel === linkText) {
          removedFilterName = checkbox.name
          removedFilterValue = checkbox.value
          break
        }
      }

      const newUrl = new URL(window.location.href)
      const newParams = newUrl.searchParams
      newParams.delete('page')

      if (!removedFilterName || !removedFilterValue) {
        const removeUrl = link.getAttribute('href')
        if (removeUrl) {
          const fallbackUrl = new URL(removeUrl, window.location.href)
          const fallbackParams = fallbackUrl.searchParams

          Array.from(currentParams.keys()).forEach(key => {
            if (key === 'page' || key === 'sort') return

            const currentValues = [...new Set(currentParams.getAll(key))]
            const fallbackValues = [...new Set(fallbackParams.getAll(key))]
            const removedValues = currentValues.filter(
              v => !fallbackValues.includes(v),
            )
            if (removedValues.length > 0) {
              removedFilterName = key
              removedFilterValue = removedValues[0]
            }
          })
        }
      }

      if (removedFilterName && removedFilterValue) {
        let urlParamName = removedFilterName

        const hasPrefix = newParams.has(`filter.v.${removedFilterName}`)
        const hasNoPrefix = newParams.has(removedFilterName)

        if (hasPrefix) {
          urlParamName = `filter.v.${removedFilterName}`
        } else if (
          !hasNoPrefix &&
          removedFilterName.indexOf('filter.v.') !== 0
        ) {
          urlParamName = `filter.v.${removedFilterName}`
        }

        const currentValues = [...new Set(newParams.getAll(urlParamName))]
        const newValues = currentValues.filter(v => v !== removedFilterValue)
        newParams.delete(urlParamName)
        newValues.forEach(v => newParams.append(urlParamName, v))
      } else {
        console.warn('Could not identify filter to remove from pill:', linkText)
        return
      }

      const cleanUrl = this.deduplicateUrlParams(newUrl.toString())
      window.history.pushState({}, '', cleanUrl)

      const pillListItem = facetRemove.closest('li')
      if (
        pillListItem &&
        activeFacetsContainer &&
        activeFacetsContainer.contains(pillListItem)
      ) {
        pillListItem.remove()
      }

      const finalUrl = new URL(cleanUrl)
      const finalParams = finalUrl.searchParams

      const allDrawerCheckboxes = drawer.querySelectorAll(
        'input[type="checkbox"].clc-ProductGridFacet_Checkbox',
      )

      allDrawerCheckboxes.forEach(checkbox => {
        const filterName = checkbox.name
        const filterValue = checkbox.value
        if (!filterName || !filterValue) return

        let allValues = finalParams.getAll(filterName)

        if (allValues.length === 0 && filterName.indexOf('filter.v.') !== 0) {
          const prefixedName = `filter.v.${filterName}`
          const prefixedValues = finalParams.getAll(prefixedName)
          if (prefixedValues.length > 0) {
            allValues = prefixedValues
          }
        }

        if (allValues.length === 0 && filterName.indexOf('filter.v.') === 0) {
          const baseName = filterName.replace(/^filter\.v\./, '')
          const baseValues = finalParams.getAll(baseName)
          if (baseValues.length > 0) {
            allValues = baseValues
          }
        }

        const uniqueValues = [...new Set(allValues)]
        checkbox.checked = uniqueValues.includes(filterValue)
      })

      if (removedFilterName && removedFilterValue) {
        const currentDrawer =
          document.querySelector(this.drawerSelector) || drawer

        let checkboxToUncheck = currentDrawer.querySelector(
          `input[type="checkbox"].clc-ProductGridFacet_Checkbox[name="${removedFilterName}"][value="${removedFilterValue}"]`,
        )

        if (!checkboxToUncheck) {
          checkboxToUncheck = currentDrawer.querySelector(
            `input[type="checkbox"][name="${removedFilterName}"][value="${removedFilterValue}"]`,
          )
        }

        if (!checkboxToUncheck) {
          checkboxToUncheck = document.querySelector(
            `input[type="checkbox"].clc-ProductGridFacet_Checkbox[name="${removedFilterName}"][value="${removedFilterValue}"]`,
          )
        }

        if (!checkboxToUncheck) {
          checkboxToUncheck = document.querySelector(
            `input[type="checkbox"][name="${removedFilterName}"][value="${removedFilterValue}"]`,
          )
        }

        if (
          !checkboxToUncheck &&
          removedFilterName.indexOf('filter.v.') !== 0
        ) {
          const ometriaFilterName = `filter.v.${removedFilterName}`
          checkboxToUncheck = currentDrawer.querySelector(
            `input[type="checkbox"][name="${ometriaFilterName}"][value="${removedFilterValue}"]`,
          )
          if (!checkboxToUncheck) {
            checkboxToUncheck = document.querySelector(
              `input[type="checkbox"][name="${ometriaFilterName}"][value="${removedFilterValue}"]`,
            )
          }
        }

        if (checkboxToUncheck) {
          checkboxToUncheck.setAttribute('data-ometria-removing', 'true')
          checkboxToUncheck.checked = false
          setTimeout(() => {
            checkboxToUncheck.removeAttribute('data-ometria-removing')
          }, 100)
        } else {
          const allCheckboxes = document.querySelectorAll(
            'input[type="checkbox"].clc-ProductGridFacet_Checkbox',
          )
          for (const cb of allCheckboxes) {
            if (
              cb.name === removedFilterName &&
              cb.value === removedFilterValue
            ) {
              cb.setAttribute('data-ometria-removing', 'true')
              cb.checked = false
              setTimeout(() => {
                cb.removeAttribute('data-ometria-removing')
              }, 100)
              break
            }
          }
        }
      }

      const priceMinInput = drawer.querySelector('input[name="price_min"]')
      const priceMaxInput = drawer.querySelector('input[name="price_max"]')
      const newPrice = finalParams.get('price')

      if (!newPrice) {
        if (priceMinInput) {
          priceMinInput.value = priceMinInput.getAttribute('min') || ''
        }
        if (priceMaxInput) {
          priceMaxInput.value = priceMaxInput.getAttribute('max') || ''
        }
        if (window.omPriceRange && window.omPriceRange.updatePriceRange) {
          window.omPriceRange.updatePriceRange()
        }
      } else {
        const [min, max] = newPrice.split('~')
        if (priceMinInput && min) priceMinInput.value = min
        if (priceMaxInput && max) priceMaxInput.value = max
        if (window.omPriceRange && window.omPriceRange.updatePriceRange) {
          window.omPriceRange.updatePriceRange()
        }
      }

      setTimeout(() => {
        const currentDrawer =
          document.querySelector(this.drawerSelector) || drawer
        this.updateActivePillsFromDrawer(currentDrawer)
      }, 10)

      this.pendingFilterUrl = cleanUrl
    } catch (error) {}
  }

  setupListeners () {
    document.addEventListener(
      'change',
      event => this.handleCheckboxChange(event),
      true,
    )
    document.addEventListener(
      'click',
      event => {
        const target = event.target
        const facetRemove = target.closest('facet-remove')
        const facetRemoveLink = target.closest(
          'a.clc-ProductGridFacets_ActiveLink',
        )

        if (facetRemove || facetRemoveLink) {
          this.handleFacetRemoveClick(event)
          return
        }

        const loadMoreButton = target.closest('.pgn-LoadMore_Button')
        if (loadMoreButton) {
          this.handleLoadMoreClick(event)
          return
        }

        const label = target.closest('label.clc-ProductGridFacet_Label')
        const item = target.closest('.clc-ProductGridFacet_Item')

        if (label || item) {
          const checkbox = label
            ? label.querySelector('input[type="checkbox"]')
            : item?.querySelector('input[type="checkbox"]')

          if (checkbox && !target.closest('[data-drawers-close]')) {
            event.stopPropagation()
            const wasChecked = checkbox.checked
            setTimeout(() => {
              const isNowChecked = checkbox.checked
              if (wasChecked !== isNowChecked || isNowChecked) {
                this.handleCheckboxChange({ target: checkbox })
              }
            }, 50)
            return
          }
        }

        this.handleApplyButtonClick(event)
      },
      true,
    )
    document.addEventListener(
      'change',
      event => this.handleSortChange(event),
      true,
    )

    document.addEventListener(
      'change',
      event => this.handlePriceInputsChange(event),
      true,
    )
    document.addEventListener(
      'click',
      event => this.handleDrawerTriggerClick(event),
      true,
    )
    window.addEventListener('popstate', () => this.handlePopState())
  }

  handleLoadMoreClick (event) {
    if (!window.graphenehc || !window.graphenehc.load) {
      return
    }

    const gridContainer = document.querySelector(this.gridSelector)
    if (!gridContainer) {
      return
    }

    const button = event.target.closest('.pgn-LoadMore_Button')
    if (!button) return

    const ometriaPaginationContainer = button.closest(
      '[data-collection-filters-form-external-el="pagination load-more"]',
    )
    if (!ometriaPaginationContainer) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()

    const href = button.getAttribute('href')
    if (!href) return

    const originalText = button.textContent.trim()
    button.disabled = true
    button.textContent = 'Loading...'

    const productList = document.querySelector('.prd-List_Items')
    if (!productList) {
      button.disabled = false
      button.textContent = originalText
      return
    }

    const currentUrl = new URL(window.location.href)
    const urlParams = new URLSearchParams(currentUrl.search)
    const currentPage = parseInt(
      urlParams.get('pg') || urlParams.get('page') || '1',
      10,
    )
    const nextPage = currentPage + 1

    urlParams.set('pg', String(nextPage))
    urlParams.delete('page')
    const nextUrl = new URL(currentUrl)
    nextUrl.search = urlParams.toString()
    window.history.pushState({}, '', nextUrl.toString())

    if (window.graphenehc?.load) {
      const paginationUrlPath = `${currentUrl.pathname}?${urlParams.toString()}`

      const tempContainer = document.createElement('div')
      tempContainer.id = 'ometria-pagination-temp'
      tempContainer.style.display = 'none'
      document.body.appendChild(tempContainer)

      const templateId = this.isSearchPage ? 'search' : 'category'

      const searchParams = new URLSearchParams(
        paginationUrlPath.split('?')[1] || '',
      )
      const qParamValue = searchParams.get('q') || ''

      const pageContext = this.buildPageContext(paginationUrlPath, qParamValue)

      window.graphenehc.load({
        template: { id: templateId, target: '#ometria-pagination-temp' },
        include: ['page'],
        url: paginationUrlPath,
        args: {
          per_page: this.config.paginationAmount,
          grid_layout: this.getCurrentGridLayout(),
          query: paginationUrlPath,
        },
        page_context: pageContext,
        success: response => {
          if (response.templates && response.templates[templateId]) {
            tempContainer.innerHTML = response.templates[templateId].html
          } else if (response.templates && response.templates.category) {
            console.warn('Template not found, using category template')
            tempContainer.innerHTML = response.templates.category.html
          }
          setTimeout(() => {
            const tempProductList = tempContainer.querySelector(
              '.prd-List_Items',
            )

            if (!tempProductList) {
              console.warn(
                'No product list found in Ometria pagination response',
              )
              tempContainer.remove()
              button.disabled = false
              button.textContent = originalText
              return
            }

            this.appendProductsFromDOM(tempProductList, nextPage)

            tempContainer.remove()

            this.updateLoadMoreButton(nextUrl)

            button.disabled = false
            button.textContent = originalText

            setTimeout(() => {
              this.reapplyGridLayout()
              this.updateProductCount()
            }, 50)
          }, 100)
        },
        error: () => {
          if (tempContainer.parentNode) {
            tempContainer.remove()
          }

          button.disabled = false
          button.textContent = originalText
        },
      })
    }
  }

  appendProductsFromDOM (sourceProductList, pageNumber) {
    try {
      const existingProductList = document.querySelector('.prd-List_Items')
      if (!existingProductList) {
        console.warn('Could not find existing product list on page')
        return
      }

      const newProducts = sourceProductList.querySelectorAll(
        '[data-collection-filters-form-external-el="item"]',
      )

      if (newProducts.length === 0) {
        console.warn('No products found in Ometria pagination response')
        return
      }

      newProducts.forEach(product => {
        product.setAttribute('data-page', String(pageNumber))
        const clonedProduct = product.cloneNode(true)
        existingProductList.appendChild(clonedProduct)
      })

      document.querySelectorAll('.acs_color_palette span').forEach(item => {
        if (!item.hasAttribute('data-init')) {
          item.addEventListener('click', e => {
            const prod = item.dataset.product
            const media = item.dataset.media
            if (media) {
              const img = document.querySelector(`img.media-${prod}`)
              if (img) {
                img.src = media
                img.srcset = ''
              }
            }
          })
          item.setAttribute('data-init', 'true')
        }
      })
    } catch (error) {
      console.warn('Error appending products from Ometria response:', error)
    }
  }

  updateLoadMoreButton (currentUrl) {
    try {
      const loadMoreContainer = document.querySelector(
        '[data-collection-filters-form-external-el="pagination load-more"]',
      )
      if (!loadMoreContainer) {
        return
      }

      loadMoreContainer.removeAttribute('data-ometria-has-more')

      const productList = document.querySelector('.prd-List_Items')
      if (!productList) {
        loadMoreContainer.innerHTML = ''
        return
      }

      const allProducts = productList.querySelectorAll(
        '[data-collection-filters-form-external-el="item"]',
      )

      const countDataEl = document.querySelector(
        '[data-product-grid-count-data]',
      )
      let totalCount = 0

      if (countDataEl && countDataEl.dataset.count) {
        totalCount = parseInt(countDataEl.dataset.count || '0', 10)
      } else if (
        this.isSearchPage &&
        typeof window !== 'undefined' &&
        window.omSearchMeta &&
        window.omSearchMeta.totalCount
      ) {
        // Fallback for search: use JSON meta from the Ometria response
        totalCount = parseInt(window.omSearchMeta.totalCount, 10) || 0
      }

      const loadedCount = allProducts.length
      const productsPerPage = this.config.paginationAmount

      if (loadedCount === 0) {
        loadMoreContainer.innerHTML = ''
        return
      }

      if (totalCount > 0 && loadedCount >= totalCount) {
        loadMoreContainer.innerHTML = ''
        return
      }
      if (totalCount === 0 && loadedCount < productsPerPage) {
        // On search, if we have JSON meta, allow Load more even when the hidden span is missing
        if (
          !this.isSearchPage ||
          !window.omSearchMeta ||
          !window.omSearchMeta.totalCount
        ) {
          loadMoreContainer.innerHTML = ''
          return
        }
      }

      const currentPage = parseInt(
        currentUrl.searchParams.get('pg') ||
          currentUrl.searchParams.get('page') ||
          '1',
        10,
      )
      const nextPage = currentPage + 1

      if (totalCount > 0) {
        // Prefer explicit page_count from meta on search, otherwise estimate from totalCount
        let estimatedPages = Math.ceil(totalCount / productsPerPage)
        if (
          this.isSearchPage &&
          window.omSearchMeta &&
          window.omSearchMeta.pageCount
        ) {
          estimatedPages =
            parseInt(window.omSearchMeta.pageCount, 10) || estimatedPages
        }
        if (nextPage > estimatedPages) {
          loadMoreContainer.innerHTML = ''
          return
        }
      }

      const nextUrl = new URL(currentUrl)
      nextUrl.searchParams.set('pg', nextPage.toString())
      nextUrl.searchParams.delete('page')
      const loadMoreText = 'Load more'
      const loadMoreDiv = document.createElement('div')
      loadMoreDiv.className = 'pgn-LoadMore'
      loadMoreDiv.innerHTML = `
        <a class="pgn-LoadMore_Button btn-Button btn-Button-primary" href="${nextUrl.toString()}" data-original-text="${loadMoreText}">
          ${loadMoreText}
        </a>
      `
      loadMoreContainer.innerHTML = ''
      loadMoreContainer.appendChild(loadMoreDiv)
      loadMoreContainer.setAttribute('data-ometria-has-more', 'true')
    } catch (error) {
      console.warn('Error updating load more button:', error)
    }
  }
}

;(function () {
  if (window.omFacetSyncBound) return
  window.omFacetSyncBound = true
  const isCollectionPage =
    window.graphenehc && document.querySelector('#collection-page-product-grid')
  const isSearchPage =
    window.graphenehc && document.querySelector('#search-page-product-grid')
  const isOmetriaEnabled = isCollectionPage || isSearchPage

  if (!isOmetriaEnabled) {
    return
  }

  const config = window.omFacetSyncConfig || {
    paginationAmount: 24,
    defaultGridLayout: 'medium',
  }

  window.ometriaFacetSync = new OmetriaFacetSync(config)

  window.ometriaFacetSync.setupDrawerCloseListener()
})()
